import { useCallback, useEffect, useRef, useState } from "react";

interface SpeechRecognitionAlternativeLike {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  item(index: number): SpeechRecognitionAlternativeLike;
  [index: number]: SpeechRecognitionAlternativeLike;
}

interface SpeechRecognitionResultListLike {
  readonly length: number;
  item(index: number): SpeechRecognitionResultLike;
  [index: number]: SpeechRecognitionResultLike;
}

interface SpeechRecognitionResultEventLike {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultListLike;
}

interface SpeechRecognitionErrorEventLike {
  readonly error: string;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  onstart: (() => void) | null;
  abort: () => void;
  start: () => void;
  stop: () => void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

type SpeechRecognitionWindow = Window & {
  SpeechRecognition?: SpeechRecognitionConstructor;
  webkitSpeechRecognition?: SpeechRecognitionConstructor;
};

export type SpeechRecognitionSupportIssue = "insecure-context" | "unsupported" | null;

interface UseSpeechRecognitionOptions {
  lang?: string;
  onResult: (text: string) => void;
}

export function useSpeechRecognition({
  lang = "zh-CN",
  onResult
}: UseSpeechRecognitionOptions) {
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const latestTranscriptRef = useRef("");
  const submittedTranscriptRef = useRef("");
  const abortingRef = useRef(false);
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [supportIssue, setSupportIssue] = useState<SpeechRecognitionSupportIssue>(null);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const speechWindow = window as SpeechRecognitionWindow;
    if (!window.isSecureContext) {
      setSupported(false);
      setSupportIssue("insecure-context");
      return undefined;
    }

    const hasSpeechRecognition = Boolean(speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition);
    setSupported(hasSpeechRecognition);
    setSupportIssue(hasSpeechRecognition ? null : "unsupported");

    return () => {
      abortingRef.current = true;
      recognitionRef.current?.abort();
    };
  }, []);

  const submitFinalTranscript = useCallback(() => {
    if (abortingRef.current) {
      abortingRef.current = false;
      return;
    }

    const text = latestTranscriptRef.current.trim();

    if (!text || text === submittedTranscriptRef.current) {
      return;
    }

    submittedTranscriptRef.current = text;
    onResult(text);
  }, [onResult]);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setListening(false);
  }, []);

  const start = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    const speechWindow = window as SpeechRecognitionWindow;
    if (!window.isSecureContext) {
      setError("公开网页需要 HTTPS 才能使用语音。");
      setSupported(false);
      setSupportIssue("insecure-context");
      return;
    }

    const RecognitionConstructor = speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;

    if (!RecognitionConstructor) {
      setError("当前运行环境不支持语音识别。");
      setSupported(false);
      setSupportIssue("unsupported");
      return;
    }

    abortingRef.current = true;
    recognitionRef.current?.abort();

    const recognition = new RecognitionConstructor();
    recognition.lang = lang;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => {
      abortingRef.current = false;
      latestTranscriptRef.current = "";
      submittedTranscriptRef.current = "";
      setError(null);
      setTranscript("");
      setListening(true);
    };
    recognition.onend = () => {
      setListening(false);
      submitFinalTranscript();
    };
    recognition.onerror = (event) => {
      setListening(false);
      setError(formatSpeechRecognitionError(event.error));
    };
    recognition.onresult = (event) => {
      const { text } = readTranscript(event);
      latestTranscriptRef.current = text;
      setTranscript(text);
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch {
      setListening(false);
      setError("语音识别启动失败，请稍后重试。");
    }
  }, [lang, submitFinalTranscript]);

  const reset = useCallback(() => {
    latestTranscriptRef.current = "";
    submittedTranscriptRef.current = "";
    setTranscript("");
    setError(null);
  }, []);

  return {
    error,
    listening,
    reset,
    start,
    stop,
    supportIssue,
    supported,
    transcript
  };
}

function readTranscript(event: SpeechRecognitionResultEventLike) {
  let finalText = "";
  let interimText = "";

  for (let index = 0; index < event.results.length; index += 1) {
    const result = event.results[index];
    const alternative = result[0] ?? result.item(0);

    if (result.isFinal) {
      finalText += alternative?.transcript ?? "";
    } else {
      interimText += alternative?.transcript ?? "";
    }
  }

  return {
    text: `${finalText}${interimText}`.trim()
  };
}

function formatSpeechRecognitionError(error: string) {
  if (error === "not-allowed" || error === "service-not-allowed") {
    return "麦克风权限未开启。";
  }

  if (error === "no-speech") {
    return "没有识别到语音。";
  }

  if (error === "audio-capture") {
    return "没有检测到可用麦克风。";
  }

  if (error === "network") {
    return "语音识别网络不可用。";
  }

  return "语音识别暂时不可用。";
}
